import { useState, useEffect, useMemo } from 'react';
import API from '../api/axios';
import TaskModal from '../components/TaskModal';
import useSortPreference from '../hooks/useSortPreference';

const priorityOrder = { Low: 0, Medium: 1, High: 2 };
const statusOrder = { 'Not Started': 0, Preparing: 1, 'In Progress': 2, 'On Hold': 3, Completed: 4 };

function compareSortValues(a, b, sortCol, sortDir) {
  let valA, valB;
  switch (sortCol) {
    case 'task_number':
      valA = a.task_number ?? null; valB = b.task_number ?? null;
      break;
    case 'name':
      valA = (a.name || '').toLowerCase(); valB = (b.name || '').toLowerCase();
      break;
    case 'folder_name':
      valA = (a.folder_name || '').toLowerCase(); valB = (b.folder_name || '').toLowerCase();
      break;
    case 'priority':
      valA = a.priority != null ? priorityOrder[a.priority] : null;
      valB = b.priority != null ? priorityOrder[b.priority] : null;
      break;
    case 'status':
      valA = a.status != null ? statusOrder[a.status] : null;
      valB = b.status != null ? statusOrder[b.status] : null;
      break;
    case 'end_date':
      valA = a.end_date ? new Date(a.end_date) : null;
      valB = b.end_date ? new Date(b.end_date) : null;
      break;
    case 'completed_at':
      valA = a.completed_at ? new Date(a.completed_at) : null;
      valB = b.completed_at ? new Date(b.completed_at) : null;
      break;
    default:
      return 0;
  }
  if (valA == null && valB == null) return 0;
  if (valA == null) return 1;
  if (valB == null) return -1;
  if (valA < valB) return sortDir === 'asc' ? -1 : 1;
  if (valA > valB) return sortDir === 'asc' ? 1 : -1;
  return 0;
}

function formatDateLocal(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString();
}

export default function CompletedPage() {
  const [tasks, setTasks] = useState([]);
  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const { sortCol, sortDir, handleSort } = useSortPreference(
    'focusly-completed-sort',
    { defaultCol: 'completed_at', defaultDir: 'desc' }
  );

  const fetchTasks = async () => {
    const { data } = await API.get('/tasks');
    setTasks(data);
  };

  useEffect(() => {
    fetchTasks();
    API.get('/folders').then(({ data }) => setFolders(data));
    API.get('/tags').then(({ data }) => setTags(data));
  }, []);

  const completedTasks = useMemo(
    () => tasks.filter((t) => t.status === 'Completed'),
    [tasks]
  );

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    await API.delete(`/tasks/${id}`);
    setShowModal(false);
    fetchTasks();
  };

  const handleEdit = (task) => {
    setEditTask(task);
    setShowModal(true);
  };

  const handleSave = async (taskData) => {
    await API.put(`/tasks/${editTask.id}`, taskData);
    setShowModal(false);
    fetchTasks();
  };

  const sortIndicator = (col) => {
    if (sortCol !== col) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  const sorted = sortCol
    ? [...completedTasks].sort((a, b) => compareSortValues(a, b, sortCol, sortDir))
    : completedTasks;

  return (
    <div className="container py-4">
      <h4 className="mb-3">Completed</h4>

      {sorted.length === 0 ? (
        <p className="text-muted">No completed tasks yet.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Type</th>
                <th style={{ width: 60, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('task_number')}>ID{sortIndicator('task_number')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>Name{sortIndicator('name')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('folder_name')}>Folder{sortIndicator('folder_name')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('priority')}>Priority{sortIndicator('priority')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('end_date')}>Due{sortIndicator('end_date')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('completed_at')}>Completed{sortIndicator('completed_at')}</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((t) => (
                <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => handleEdit(t)}>
                  <td className="text-center">
                    <span className="badge bg-success" style={{ fontSize: '0.65rem' }}>Task</span>
                  </td>
                  <td><small className="text-muted">{t.task_number}</small></td>
                  <td><div className="fw-semibold">{t.name}</div></td>
                  <td><small>{t.folder_name}</small></td>
                  <td><span className="badge bg-secondary">{t.priority || '-'}</span></td>
                  <td><small>{t.end_date ? formatDateLocal(t.end_date) : '-'}</small></td>
                  <td><small>{t.completed_at ? formatDateLocal(t.completed_at) : '-'}</small></td>
                  <td></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <TaskModal
          task={editTask}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setShowModal(false)}
          folders={folders}
          currentFolderId={editTask?.folder_id}
          tags={tags}
        />
      )}
    </div>
  );
}
